// Bob Volman Price Action Scalping Engine (Frontend Client-Side & Replay)
// Methodology:
// 1. 20 EMA: Primary trend direction, dynamic support/resistance & equilibrium.
// 2. Swing Pivots: Automatic detection of swing highs/lows for Barriers & Trendlines.
// 3. Buildup Squeeze: Tight compression (ATR <= 0.85) hugging 20 EMA against barrier/trendline.
// 4. 2 Core High-Probability Setups: BB (Block Break), TB (Trendline Break).
// 5. Ultra-tight SL behind Buildup + Single TP Target (1.5R).

export function getPipMultiplier(price) {
  if (price < 5.0) return 10000.0;     // EURUSD, GBPUSD (0.0001 = 1 pip)
  if (price < 500.0) return 100.0;     // USDJPY (0.01 = 1 pip)
  if (price < 15000.0) return 10.0;    // Gold XAUUSD ($1.00 = 10 pips, 0.1 = 1 pip)
  return 1.0;                          // BTCUSD ($1 = 1 pip)
}

export function getPriceDecimals(price) {
  if (price < 5.0) return 5;
  if (price < 500.0) return 3;
  if (price < 15000.0) return 2;
  return 2;
}

export function calcEMA(data, period = 20) {
  const n = data.length;
  if (n === 0) return [];
  const k = 2 / (period + 1);
  const res = new Array(n);
  let prevEma = data[0] || 0;
  for (let i = 0; i < n; i++) {
    if (i === 0) {
      prevEma = data[0];
    } else {
      prevEma = data[i] * k + prevEma * (1 - k);
    }
    res[i] = prevEma;
  }
  return res;
}

export function calcSMA(data, period = 200) {
  const n = data.length;
  if (n === 0) return [];
  const res = new Array(n);
  const p = Math.max(1, period);
  let currSum = 0;
  for (let i = 0; i < n; i++) {
    currSum += (data[i] || 0);
    if (i >= p) {
      currSum -= (data[i - p] || 0);
      res[i] = currSum / p;
    } else {
      res[i] = currSum / (i + 1);
    }
  }
  return res;
}

export function calcATR(highs, lows, closes, period = 14) {
  const n = closes.length;
  if (n === 0) return [];
  const tr = new Array(n);
  tr[0] = highs[0] - lows[0];
  for (let i = 1; i < n; i++) {
    const hl = highs[i] - lows[i];
    const hc = Math.abs(highs[i] - closes[i - 1]);
    const lc = Math.abs(lows[i] - closes[i - 1]);
    tr[i] = Math.max(hl, hc, lc);
  }

  const atr = new Array(n);
  const p = Math.max(1, period);
  if (n < p) return tr;

  let sum = 0;
  for (let i = 0; i < p; i++) sum += tr[i];
  atr[p - 1] = sum / p;

  for (let i = p; i < n; i++) {
    atr[i] = (atr[i - 1] * (p - 1) + tr[i]) / p;
  }
  for (let i = 0; i < p - 1; i++) {
    atr[i] = atr[p - 1];
  }
  return atr;
}

export function calculateBobVolman(bars, params = {}) {
  const emaPeriod = Number(params.volmanEmaPeriod ?? params.volman_ema_period ?? 20);
  const pivotLeft = Number(params.volmanPivotLength ?? params.volman_pivot_length ?? params.volmanPivotLeft ?? params.volman_pivot_left ?? 3);
  const pivotRight = Number(params.volmanPivotLength ?? params.volman_pivot_length ?? params.volmanPivotRight ?? params.volman_pivot_right ?? 3);
  const barrierTolerancePips = Number(params.volmanBarrierTolerancePips ?? params.volman_barrier_tolerance_pips ?? 8.0);
  const minBarrierTouches = Number(params.volmanMinBarrierTouches ?? params.volman_min_barrier_touches ?? 2);
  const minBuildupBars = Number(params.volmanMinBuildupBars ?? params.volman_min_buildup_bars ?? 3);
  const maxBuildupBars = Number(params.volmanMaxBuildupBars ?? params.volman_max_buildup_bars ?? 12);
  const maxBuildupAtrMult = Number(params.volmanMaxBuildupAtrMult ?? params.volman_max_buildup_atr_mult ?? 0.85);
  const r1 = Number(params.volmanR1 ?? params.volman_r1 ?? 1.5);
  const slBufferPips = Number(params.volmanSlBufferPips ?? params.volman_sl_buffer_pips ?? 5.0);
  const slFilterEnable = Boolean(params.volmanSlFilterEnable !== undefined ? params.volmanSlFilterEnable : (params.volman_sl_filter_enable !== undefined ? params.volman_sl_filter_enable : (params.slFilterEnable !== undefined ? params.slFilterEnable : true)));
  const minRiskPips = Number(params.volmanMinRiskPips ?? params.volman_min_risk_pips ?? 3.0);
  const maxRiskPips = Number(params.volmanMaxRiskPips ?? params.volman_max_risk_pips ?? 150.0);
  const cooldownBars = Number(params.volmanCooldownBars ?? params.volman_cooldown_bars ?? 5);
  const enableBB = Boolean(params.volmanEnableBB !== undefined ? params.volmanEnableBB : (params.volman_enable_bb !== undefined ? params.volman_enable_bb : true));
  const enableTB = Boolean(params.volmanEnableTB !== undefined ? params.volmanEnableTB : (params.volman_enable_tb !== undefined ? params.volman_enable_tb : true));
  const enableBreakoutFilter = Boolean(params.volmanBreakoutCandleFilter !== undefined ? params.volmanBreakoutCandleFilter : (params.volman_breakout_candle_filter !== undefined ? params.volman_breakout_candle_filter : true));
  const minBodyRatio = Number(params.volmanMinBodyRatio ?? params.volman_min_body_ratio ?? 50); // %
  const minBodyBeyondPct = Number(params.volmanMinBodyBeyondPct ?? params.volman_min_body_beyond_pct ?? 30); // %
  const maxRejectionWick = Number(params.volmanMaxRejectionWick ?? params.volman_max_rejection_wick ?? 25); // %

  // Dynamic Limit parameters (Retest Pullback Engine)
  const dynamicLimitEnable = Boolean(params.volmanDynamicLimitEnable !== undefined ? params.volmanDynamicLimitEnable : (params.volman_dynamic_limit_enable !== undefined ? params.volman_dynamic_limit_enable : (params.dynamicLimitEnable !== undefined ? params.dynamicLimitEnable : false)));
  const limitThresholdPips = Number(params.volmanLimitThresholdPips ?? params.volman_limit_threshold_pips ?? params.limitThresholdPips ?? 80.0);
  const limitPullbackPct = Number(params.volmanLimitPullbackPct ?? params.volman_limit_pullback_pct ?? params.limitPullbackPct ?? 40.0);
  const limitCancelBars = Number(params.volmanLimitCancelBars ?? params.volman_limit_cancel_bars ?? params.limitCancelBars ?? 20);

  // MA / Trend Filter parameters (Price > MA for BUY, Price < MA for SELL)
  const maFilterEnable = Boolean(params.volmanMaFilterEnable !== undefined ? params.volmanMaFilterEnable : (params.volman_ma_filter_enable !== undefined ? params.volman_ma_filter_enable : (params.volman_ema200_filter !== undefined ? params.volman_ema200_filter : (params.maFilterEnable !== undefined ? params.maFilterEnable : false))));
  const maPeriod = Number(params.volmanMaPeriod ?? params.volman_ma_period ?? params.volman_ema200_period ?? params.maPeriod ?? 200);
  const maType = String(params.volmanMaType || params.volman_ma_type || params.maType || "EMA").toUpperCase();

  // Max Consecutive Same-Direction Orders Filter
  const maxConsecutiveEnable = Boolean(params.volmanMaxConsecutiveEnable !== undefined ? params.volmanMaxConsecutiveEnable : (params.volman_max_consecutive_enable !== undefined ? params.volman_max_consecutive_enable : false));
  const maxConsecutiveSameDir = Number(params.volmanMaxConsecutiveSameDir ?? params.volman_max_consecutive_same_dir ?? 3);

  const n = bars.length;
  if (n < 30) {
    return {
      ema20: [],
      ma_series: [],
      signals: [],
      recent_signals: [],
      all_signals: [],
      latest_signal: null,
      latestSignal: null,
      chart_markers: [],
      chartMarkers: [],
      visual_lines: [],
      visualLines: [],
      barriers: [],
      trendlines: [],
      buildup_boxes: [],
      market_bias: "NEUTRAL",
      streaks: { max_win_streak: 0, max_loss_streak: 0 },
      backtest: {},
      backtest_windows: {},
    };
  }

  const refP = Number(bars[n - 1].close || 2000.0);
  const pipMultiplier = getPipMultiplier(refP);
  const decimals = getPriceDecimals(refP);
  const barrierTolPrice = barrierTolerancePips / pipMultiplier;
  const slBufPrice = slBufferPips / pipMultiplier;

  const opens = bars.map((b) => Number(b.open));
  const highs = bars.map((b) => Number(b.high));
  const lows = bars.map((b) => Number(b.low));
  const closes = bars.map((b) => Number(b.close));
  const times = bars.map((b) => Number(b.time || 0));

  const ema20 = calcEMA(closes, emaPeriod);
  const atr14 = calcATR(highs, lows, closes, 14);
  const maSeries = maType === "SMA" ? calcSMA(closes, maPeriod) : calcEMA(closes, maPeriod);

  // 1. Detect Pivot Highs and Pivot Lows (Standard Bob Volman Swing Pivots)
  const pivotHighs = [];
  const pivotLows = [];
  const pl = Math.max(1, pivotLeft);
  const pr = Math.max(1, pivotRight);

  for (let i = pl; i < n - pr; i++) {
    const currH = highs[i];
    const currL = lows[i];

    // Pivot High
    let isPh = true;
    for (let leftI = i - pl; leftI < i; leftI++) {
      if (highs[leftI] >= currH) {
        isPh = false;
        break;
      }
    }
    if (isPh) {
      for (let rightI = i + 1; rightI <= i + pr; rightI++) {
        if (highs[rightI] > currH) {
          isPh = false;
          break;
        }
      }
    }
    if (isPh) {
      pivotHighs.push({ index: i, time: times[i], price: currH });
    }

    // Pivot Low
    let isPl = true;
    for (let leftI = i - pl; leftI < i; leftI++) {
      if (lows[leftI] <= currL) {
        isPl = false;
        break;
      }
    }
    if (isPl) {
      for (let rightI = i + 1; rightI <= i + pr; rightI++) {
        if (lows[rightI] < currL) {
          isPl = false;
          break;
        }
      }
    }
    if (isPl) {
      pivotLows.push({ index: i, time: times[i], price: currL });
    }
  }

  // 2. Scan Setups
  const rawSignals = [];
  const allBarriers = [];
  const allTrendlines = [];
  const allBuildups = [];
  let lastSignalBar = -999;

  let consecutiveDir = null;
  let consecutiveCount = 0;

  for (let i = Math.max(25, emaPeriod + 5); i < n; i++) {
    if (i - lastSignalBar < cooldownBars) continue;

    const currAtr = atr14[i] || (highs[i] - lows[i]) || 1.0;
    const cLen = highs[i] - lows[i];
    const cBody = Math.abs(closes[i] - opens[i]);
    const cBodyRatio = cLen > 0 ? (cBody / cLen) * 100 : 0;
    const currEma = ema20[i];
    const prevEma = ema20[i - 1];
    const prevEma4 = ema20[Math.max(0, i - 4)] || prevEma;
    const emaSlopeUp = currEma >= prevEma && currEma >= prevEma4;
    const emaSlopeDown = currEma <= prevEma && currEma <= prevEma4;

    const currMa = (maSeries && maSeries[i] !== undefined) ? maSeries[i] : closes[i];
    const passBuyMa = (!maFilterEnable) || (closes[i] >= currMa);
    const passSellMa = (!maFilterEnable) || (closes[i] <= currMa);

    let foundSignal = null;

    // ----------------------------------------------------
    // SETUP A: Block / Barrier Break (BB)
    // ----------------------------------------------------
    if (enableBB && !foundSignal) {
      // Resistance Barrier Break (BUY)
      const recentPhs = pivotHighs.filter((p) => p.index >= i - 60 && p.index <= i - 1);
      if (passBuyMa && recentPhs.length >= minBarrierTouches && emaSlopeUp && closes[i] > opens[i]) {
        for (let idx = recentPhs.length - 1; idx >= 0; idx--) {
          const phMain = recentPhs[idx];
          const cluster = recentPhs.filter((p) => Math.abs(p.price - phMain.price) <= barrierTolPrice);
          if (cluster.length >= minBarrierTouches) {
            const barrierLevel = Math.max(...cluster.map((p) => p.price));
            for (let bLen = minBuildupBars; bLen <= maxBuildupBars; bLen++) {
              if (i - bLen < 0) break;
              const bHighs = highs.slice(i - bLen, i);
              const bLows = lows.slice(i - bLen, i);
              const bEmas = ema20.slice(i - bLen, i);

              const maxBHigh = Math.max(...bHighs);
              const minBLow = Math.min(...bLows);
              const bRange = maxBHigh - minBLow;

              const hugsEma = bLows.some((l, k) => l <= bEmas[k] + 0.3 * currAtr);
              const compressed = bRange <= maxBuildupAtrMult * currAtr * 2.2;
              const underBarrier = maxBHigh <= barrierLevel + 0.3 * barrierTolPrice;

              if (hugsEma && compressed && underBarrier && closes[i] > barrierLevel) {
                // Breakout Candle Confirmation Filter
                const upperWickRatio = cLen > 0 ? ((highs[i] - closes[i]) / cLen) * 100 : 0;
                const bodyBeyondPct = cBody > 0 ? ((closes[i] - Math.max(opens[i], barrierLevel)) / cBody) * 100 : 0;
                const passesBreakout = !enableBreakoutFilter || (
                  cBodyRatio >= minBodyRatio &&
                  upperWickRatio <= maxRejectionWick &&
                  bodyBeyondPct >= minBodyBeyondPct
                );
                if (!passesBreakout) continue;

                const marketEntry = Number(closes[i].toFixed(decimals));
                const slPrice = Number((minBLow - slBufPrice).toFixed(decimals));
                const marketRiskDiff = marketEntry - slPrice;
                const marketRiskPips = Number((marketRiskDiff * pipMultiplier).toFixed(1));

                if (!slFilterEnable || (marketRiskPips >= minRiskPips && marketRiskPips <= maxRiskPips)) {
                  let entryMode = "MARKET";
                  let entryPrice = marketEntry;

                  if (dynamicLimitEnable && marketRiskPips > limitThresholdPips) {
                    entryMode = "LIMIT";
                    const pct = limitPullbackPct / 100.0;
                    entryPrice = Number((marketEntry - pct * (marketEntry - slPrice)).toFixed(decimals));
                  }

                  const riskDiff = entryPrice - slPrice;
                  const riskPips = Number((riskDiff * pipMultiplier).toFixed(1));
                  const tpPrice = Number((marketEntry + r1 * marketRiskDiff).toFixed(decimals));
                  const rewardDiff = tpPrice - entryPrice;
                  const rewardPips = Number((rewardDiff * pipMultiplier).toFixed(1));

                  foundSignal = {
                    index: i,
                    time: times[i],
                    action: "BUY",
                    setup_type: "BB",
                    setup_name: "Block Break (BB)",
                    entry_mode: entryMode,
                    market_entry: marketEntry,
                    market_risk_pips: marketRiskPips,
                    entry: entryPrice,
                    price: entryPrice,
                    order_price: entryPrice,
                    sl: slPrice,
                    tp: tpPrice,
                    tp1: tpPrice,
                    risk_pips: riskPips,
                    riskPips: riskPips,
                    reward_pips: rewardPips,
                    limit_cancel_bars: limitCancelBars,
                    limit_pullback_pct: limitPullbackPct,
                    barrier_level: Number(barrierLevel.toFixed(decimals)),
                    buildup_bars: bLen,
                    ma_filter: maFilterEnable,
                    ma_val: Number(currMa.toFixed(decimals)),
                    ma_period: maPeriod,
                    ma_type: maType,
                    buildup_box: {
                      x1: times[i - bLen],
                      x2: times[i],
                      y1: Number(maxBHigh.toFixed(decimals)),
                      y2: Number(minBLow.toFixed(decimals)),
                      type: "BUY",
                    },
                  };
                  allBarriers.push({
                    y: Number(barrierLevel.toFixed(decimals)),
                    x1: times[cluster[0].index],
                    x2: times[i],
                    type: "RESISTANCE",
                    touches: cluster.length,
                  });
                  allBuildups.push(foundSignal.buildup_box);
                  break;
                }
              }
            }
            if (foundSignal) break;
          }
        }
      }

      // Support Barrier Break (SELL)
      if (!foundSignal) {
        const recentPls = pivotLows.filter((p) => p.index >= i - 60 && p.index <= i - 1);
        if (passSellMa && recentPls.length >= minBarrierTouches && emaSlopeDown && closes[i] < opens[i]) {
          for (let idx = recentPls.length - 1; idx >= 0; idx--) {
            const plMain = recentPls[idx];
            const cluster = recentPls.filter((p) => Math.abs(p.price - plMain.price) <= barrierTolPrice);
            if (cluster.length >= minBarrierTouches) {
              const barrierLevel = Math.min(...cluster.map((p) => p.price));
              for (let bLen = minBuildupBars; bLen <= maxBuildupBars; bLen++) {
                if (i - bLen < 0) break;
                const bHighs = highs.slice(i - bLen, i);
                const bLows = lows.slice(i - bLen, i);
                const bEmas = ema20.slice(i - bLen, i);

                const maxBHigh = Math.max(...bHighs);
                const minBLow = Math.min(...bLows);
                const bRange = maxBHigh - minBLow;

                const hugsEma = bHighs.some((h, k) => h >= bEmas[k] - 0.3 * currAtr);
                const compressed = bRange <= maxBuildupAtrMult * currAtr * 2.2;
                const aboveBarrier = minBLow >= barrierLevel - 0.3 * barrierTolPrice;

                if (hugsEma && compressed && aboveBarrier && closes[i] < barrierLevel) {
                  // Breakout Candle Confirmation Filter
                  const lowerWickRatio = cLen > 0 ? ((closes[i] - lows[i]) / cLen) * 100 : 0;
                  const bodyBeyondPct = cBody > 0 ? ((Math.min(opens[i], barrierLevel) - closes[i]) / cBody) * 100 : 0;
                  const passesBreakout = !enableBreakoutFilter || (
                    cBodyRatio >= minBodyRatio &&
                    lowerWickRatio <= maxRejectionWick &&
                    bodyBeyondPct >= minBodyBeyondPct
                  );
                  if (!passesBreakout) continue;

                  const marketEntry = Number(closes[i].toFixed(decimals));
                  const slPrice = Number((maxBHigh + slBufPrice).toFixed(decimals));
                  const marketRiskDiff = slPrice - marketEntry;
                  const marketRiskPips = Number((marketRiskDiff * pipMultiplier).toFixed(1));

                  if (!slFilterEnable || (marketRiskPips >= minRiskPips && marketRiskPips <= maxRiskPips)) {
                    let entryMode = "MARKET";
                    let entryPrice = marketEntry;

                    if (dynamicLimitEnable && marketRiskPips > limitThresholdPips) {
                      entryMode = "LIMIT";
                      const pct = limitPullbackPct / 100.0;
                      entryPrice = Number((marketEntry + pct * (slPrice - marketEntry)).toFixed(decimals));
                    }

                    const riskDiff = slPrice - entryPrice;
                    const riskPips = Number((riskDiff * pipMultiplier).toFixed(1));
                    const tpPrice = Number((marketEntry - r1 * marketRiskDiff).toFixed(decimals));
                    const rewardDiff = entryPrice - tpPrice;
                    const rewardPips = Number((rewardDiff * pipMultiplier).toFixed(1));

                    foundSignal = {
                      index: i,
                      time: times[i],
                      action: "SELL",
                      setup_type: "BB",
                      setup_name: "Block Break (BB)",
                      entry_mode: entryMode,
                      market_entry: marketEntry,
                      market_risk_pips: marketRiskPips,
                      entry: entryPrice,
                      price: entryPrice,
                      order_price: entryPrice,
                      sl: slPrice,
                      tp: tpPrice,
                      tp1: tpPrice,
                      risk_pips: riskPips,
                      riskPips: riskPips,
                      reward_pips: rewardPips,
                      limit_cancel_bars: limitCancelBars,
                      limit_pullback_pct: limitPullbackPct,
                      barrier_level: Number(barrierLevel.toFixed(decimals)),
                      buildup_bars: bLen,
                      ma_filter: maFilterEnable,
                      ma_val: Number(currMa.toFixed(decimals)),
                      ma_period: maPeriod,
                      ma_type: maType,
                      buildup_box: {
                        x1: times[i - bLen],
                        x2: times[i],
                        y1: Number(maxBHigh.toFixed(decimals)),
                        y2: Number(minBLow.toFixed(decimals)),
                        type: "SELL",
                      },
                    };
                    allBarriers.push({
                      y: Number(barrierLevel.toFixed(decimals)),
                      x1: times[cluster[0].index],
                      x2: times[i],
                      type: "SUPPORT",
                      touches: cluster.length,
                    });
                    allBuildups.push(foundSignal.buildup_box);
                    break;
                  }
                }
              }
              if (foundSignal) break;
            }
          }
        }
      }
    }

    // ----------------------------------------------------
    // SETUP B: Trendline Break (TB)
    // ----------------------------------------------------
    if (enableTB && !foundSignal) {
      // 1. Bullish TB: Descending trendline broken
      if (passBuyMa && emaSlopeUp && closes[i] > opens[i] && closes[i] > currEma) {
        const phStart = pivotHighs.filter((p) => p.index >= i - 60 && p.index <= i - 2);

        for (let idx2 = phStart.length - 1; idx2 >= 1; idx2--) {
          const p2 = phStart[idx2];
          if (i - p2.index < 3 || i - p2.index > 25) continue;

          for (let idx1 = idx2 - 1; idx1 >= Math.max(0, idx2 - 6); idx1--) {
            const p1 = phStart[idx1];
            const barDist = p2.index - p1.index;
            if (barDist < 6 || barDist > 35) continue;
            // Descending: P1 > P2
            if (p1.price <= p2.price) continue;

            const slope = (p2.price - p1.price) / barDist;
            if (Math.abs(slope) < 0.02 * currAtr || Math.abs(slope) > 1.0 * currAtr) continue;

            // CRITICAL NO-PIERCING CHECK:
            let isPierced = false;
            for (let k = p1.index + 1; k < i; k++) {
              const tlK = p1.price + slope * (k - p1.index);
              if (highs[k] > tlK + 0.15 * currAtr) {
                isPierced = true;
                break;
              }
            }
            if (isPierced) continue;

            const tlCurrVal = p2.price + slope * (i - p2.index);
            const minRecentLow = Math.min(...lows.slice(Math.max(0, i - 3), i + 1));
            if (tlCurrVal < minRecentLow) continue;

            if (closes[i] > tlCurrVal && closes[i - 1] <= tlCurrVal + 0.25 * currAtr) {
              // Breakout Candle Confirmation Filter
              const upperWickRatio = cLen > 0 ? ((highs[i] - closes[i]) / cLen) * 100 : 0;
              const bodyBeyondPct = cBody > 0 ? ((closes[i] - Math.max(opens[i], tlCurrVal)) / cBody) * 100 : 0;
              const passesBreakout = !enableBreakoutFilter || (
                cBodyRatio >= minBodyRatio &&
                upperWickRatio <= maxRejectionWick &&
                bodyBeyondPct >= minBodyBeyondPct
              );
              if (!passesBreakout) continue;

              for (let bLen = minBuildupBars; bLen <= maxBuildupBars; bLen++) {
                if (i - bLen < 0) break;
                const bHighs = highs.slice(i - bLen, i);
                const bLows = lows.slice(i - bLen, i);
                const bEmas = ema20.slice(i - bLen, i);
                const minBLow = Math.min(...bLows);
                const maxBHigh = Math.max(...bHighs);

                const hugsEma = bLows.some((l, k) => l <= bEmas[k] + 0.35 * currAtr);
                const compressed = maxBHigh - minBLow <= maxBuildupAtrMult * currAtr * 2.2;

                if (hugsEma && compressed) {
                  const marketEntry = Number(closes[i].toFixed(decimals));
                  const slPrice = Number((minBLow - slBufPrice).toFixed(decimals));
                  const marketRiskDiff = marketEntry - slPrice;
                  const marketRiskPips = Number((marketRiskDiff * pipMultiplier).toFixed(1));

                  if (!slFilterEnable || (marketRiskPips >= minRiskPips && marketRiskPips <= maxRiskPips)) {
                    let entryMode = "MARKET";
                    let entryPrice = marketEntry;

                    if (dynamicLimitEnable && marketRiskPips > limitThresholdPips) {
                      entryMode = "LIMIT";
                      const pct = limitPullbackPct / 100.0;
                      entryPrice = Number((marketEntry - pct * (marketEntry - slPrice)).toFixed(decimals));
                    }

                    const riskDiff = entryPrice - slPrice;
                    const riskPips = Number((riskDiff * pipMultiplier).toFixed(1));
                    const tpPrice = Number((marketEntry + r1 * marketRiskDiff).toFixed(decimals));
                    const rewardDiff = tpPrice - entryPrice;
                    const rewardPips = Number((rewardDiff * pipMultiplier).toFixed(1));

                    foundSignal = {
                      index: i,
                      time: times[i],
                      action: "BUY",
                      setup_type: "TB",
                      setup_name: "Trendline Break (TB)",
                      entry_mode: entryMode,
                      market_entry: marketEntry,
                      market_risk_pips: marketRiskPips,
                      entry: entryPrice,
                      price: entryPrice,
                      order_price: entryPrice,
                      sl: slPrice,
                      tp: tpPrice,
                      tp1: tpPrice,
                      risk_pips: riskPips,
                      riskPips: riskPips,
                      reward_pips: rewardPips,
                      limit_cancel_bars: limitCancelBars,
                      limit_pullback_pct: limitPullbackPct,
                      trendline_level: Number(tlCurrVal.toFixed(decimals)),
                      buildup_bars: bLen,
                      ma_filter: maFilterEnable,
                      ma_val: Number(currMa.toFixed(decimals)),
                      ma_period: maPeriod,
                      ma_type: maType,
                      buildup_box: {
                        x1: times[i - bLen],
                        x2: times[i],
                        y1: Number(maxBHigh.toFixed(decimals)),
                        y2: Number(minBLow.toFixed(decimals)),
                        type: "BUY",
                      },
                    };
                    allTrendlines.push({
                      x1: times[p1.index],
                      y1: Number(p1.price.toFixed(decimals)),
                      x2: times[i],
                      y2: Number(tlCurrVal.toFixed(decimals)),
                      type: "DESCENDING",
                      touches: 2,
                    });
                    allBuildups.push(foundSignal.buildup_box);
                    break;
                  }
                }
              }
              if (foundSignal) break;
            }
          }
          if (foundSignal) break;
        }
      }

      // 2. Bearish TB: Ascending trendline broken
      if (!foundSignal && passSellMa && emaSlopeDown && closes[i] < opens[i] && closes[i] < currEma) {
        const plStart = pivotLows.filter((p) => p.index >= i - 60 && p.index <= i - 2);

        for (let idx2 = plStart.length - 1; idx2 >= 1; idx2--) {
          const p2 = plStart[idx2];
          if (i - p2.index < 3 || i - p2.index > 25) continue;

          for (let idx1 = idx2 - 1; idx1 >= Math.max(0, idx2 - 6); idx1--) {
            const p1 = plStart[idx1];
            const barDist = p2.index - p1.index;
            if (barDist < 6 || barDist > 35) continue;
            // Ascending: P2 > P1
            if (p2.price <= p1.price) continue;

            const slope = (p2.price - p1.price) / barDist;
            if (Math.abs(slope) < 0.02 * currAtr || Math.abs(slope) > 1.0 * currAtr) continue;

            // CRITICAL NO-PIERCING CHECK:
            let isPierced = false;
            for (let k = p1.index + 1; k < i; k++) {
              const tlK = p1.price + slope * (k - p1.index);
              if (lows[k] < tlK - 0.15 * currAtr) {
                isPierced = true;
                break;
              }
            }
            if (isPierced) continue;

            const tlCurrVal = p2.price + slope * (i - p2.index);

            if (closes[i] < tlCurrVal && closes[i - 1] >= tlCurrVal - 0.25 * currAtr) {
              // Breakout Candle Confirmation Filter
              const lowerWickRatio = cLen > 0 ? ((closes[i] - lows[i]) / cLen) * 100 : 0;
              const bodyBeyondPct = cBody > 0 ? ((Math.min(opens[i], tlCurrVal) - closes[i]) / cBody) * 100 : 0;
              const passesBreakout = !enableBreakoutFilter || (
                cBodyRatio >= minBodyRatio &&
                lowerWickRatio <= maxRejectionWick &&
                bodyBeyondPct >= minBodyBeyondPct
              );
              if (!passesBreakout) continue;

              for (let bLen = minBuildupBars; bLen <= maxBuildupBars; bLen++) {
                if (i - bLen < 0) break;
                const bHighs = highs.slice(i - bLen, i);
                const bLows = lows.slice(i - bLen, i);
                const bEmas = ema20.slice(i - bLen, i);
                const minBLow = Math.min(...bLows);
                const maxBHigh = Math.max(...bHighs);

                const hugsEma = bHighs.some((h, k) => h >= bEmas[k] - 0.35 * currAtr);
                const compressed = maxBHigh - minBLow <= maxBuildupAtrMult * currAtr * 2.2;

                if (hugsEma && compressed) {
                  const marketEntry = Number(closes[i].toFixed(decimals));
                  const slPrice = Number((maxBHigh + slBufPrice).toFixed(decimals));
                  const marketRiskDiff = slPrice - marketEntry;
                  const marketRiskPips = Number((marketRiskDiff * pipMultiplier).toFixed(1));

                  if (!slFilterEnable || (marketRiskPips >= minRiskPips && marketRiskPips <= maxRiskPips)) {
                    let entryMode = "MARKET";
                    let entryPrice = marketEntry;

                    if (dynamicLimitEnable && marketRiskPips > limitThresholdPips) {
                      entryMode = "LIMIT";
                      const pct = limitPullbackPct / 100.0;
                      entryPrice = Number((marketEntry + pct * (slPrice - marketEntry)).toFixed(decimals));
                    }

                    const riskDiff = slPrice - entryPrice;
                    const riskPips = Number((riskDiff * pipMultiplier).toFixed(1));
                    const tpPrice = Number((marketEntry - r1 * marketRiskDiff).toFixed(decimals));
                    const rewardDiff = entryPrice - tpPrice;
                    const rewardPips = Number((rewardDiff * pipMultiplier).toFixed(1));

                    foundSignal = {
                      index: i,
                      time: times[i],
                      action: "SELL",
                      setup_type: "TB",
                      setup_name: "Trendline Break (TB)",
                      entry_mode: entryMode,
                      market_entry: marketEntry,
                      market_risk_pips: marketRiskPips,
                      entry: entryPrice,
                      price: entryPrice,
                      order_price: entryPrice,
                      sl: slPrice,
                      tp: tpPrice,
                      tp1: tpPrice,
                      risk_pips: riskPips,
                      riskPips: riskPips,
                      reward_pips: rewardPips,
                      limit_cancel_bars: limitCancelBars,
                      limit_pullback_pct: limitPullbackPct,
                      trendline_level: Number(tlCurrVal.toFixed(decimals)),
                      buildup_bars: bLen,
                      ma_filter: maFilterEnable,
                      ma_val: Number(currMa.toFixed(decimals)),
                      ma_period: maPeriod,
                      ma_type: maType,
                      buildup_box: {
                        x1: times[i - bLen],
                        x2: times[i],
                        y1: Number(maxBHigh.toFixed(decimals)),
                        y2: Number(minBLow.toFixed(decimals)),
                        type: "SELL",
                      },
                    };
                    allTrendlines.push({
                      x1: times[p1.index],
                      y1: Number(p1.price.toFixed(decimals)),
                      x2: times[i],
                      y2: Number(tlCurrVal.toFixed(decimals)),
                      type: "ASCENDING",
                      touches: 2,
                    });
                    allBuildups.push(foundSignal.buildup_box);
                    break;
                  }
                }
              }
              if (foundSignal) break;
            }
          }
          if (foundSignal) break;
        }
      }
    }

    if (foundSignal) {
      if (maxConsecutiveEnable && maxConsecutiveSameDir > 0) {
        const sigAct = foundSignal.action;
        if (sigAct === consecutiveDir && consecutiveCount >= maxConsecutiveSameDir) {
          // Exceeded consecutive limit -> Block signal
          if (allBuildups.length > 0) {
            allBuildups.pop();
          }
          if (foundSignal.setup_type === "BB" && allBarriers.length > 0) {
            allBarriers.pop();
          } else if (foundSignal.setup_type === "TB" && allTrendlines.length > 0) {
            allTrendlines.pop();
          }
          foundSignal = null;
        } else {
          if (sigAct === consecutiveDir) {
            consecutiveCount += 1;
          } else {
            consecutiveDir = sigAct;
            consecutiveCount = 1;
          }
        }
      }
    }

    if (foundSignal) {
      rawSignals.push(foundSignal);
      lastSignalBar = i;
    }
  }

  // 3. Trade Simulation & Status
  const completedTrades = [];
  const chartMarkers = [];

  for (const sig of rawSignals) {
    const idx = sig.index;
    const isBuy = sig.action === "BUY";
    const isLimit = sig.entry_mode === "LIMIT";
    const entryP = sig.entry;
    const slP = sig.sl;
    const tp1P = sig.tp1;

    const tr = { ...sig };
    tr.status = "OPEN";
    tr.win = false;
    tr.exit_time = null;
    tr.exit_price = null;
    tr.pnl_pips = 0.0;
    tr.pnlPips = 0.0;
    tr.exit_reason = null;

    let filled = !isLimit;
    let fillIdx = idx;

    // Handle LIMIT Order waiting & fill simulation
    if (isLimit) {
      const maxK = Math.min(n, idx + 1 + limitCancelBars);
      for (let k = idx + 1; k < maxK; k++) {
        if (isBuy) {
          if (lows[k] <= entryP) {
            filled = true;
            fillIdx = k;
            tr.fill_time = times[k];
            tr.fillTime = times[k];
            tr.fill_idx = k;
            break;
          }
        } else {
          if (highs[k] >= entryP) {
            filled = true;
            fillIdx = k;
            tr.fill_time = times[k];
            tr.fillTime = times[k];
            tr.fill_idx = k;
            break;
          }
        }
      }

      if (!filled) {
        if (n - 1 - idx <= limitCancelBars) {
          tr.status = "PENDING_LIMIT";
          tr.exit_time = times[n - 1];
          tr.pnl_pips = 0.0;
          tr.pnlPips = 0.0;
          tr.win = false;
        } else {
          tr.status = "TIMEOUT";
          tr.exit_time = times[Math.min(n - 1, idx + limitCancelBars)];
          tr.pnl_pips = 0.0;
          tr.pnlPips = 0.0;
          tr.win = false;
        }

        completedTrades.push(tr);

        const setupTag = sig.setup_type || "BV";
        const markerText = isBuy ? `LIMIT ${setupTag} BUY` : `LIMIT ${setupTag} SELL`;
        chartMarkers.push({
          time: sig.time,
          position: isBuy ? "belowBar" : "aboveBar",
          color: tr.status === "PENDING_LIMIT" ? "#f59e0b" : (isBuy ? "#06b6d4" : "#f43f5e"),
          shape: isBuy ? "arrowUp" : "arrowDown",
          text: markerText,
          id: `bv_${sig.time}`,
          size: 1,
        });
        continue;
      }
    }

    let isFinished = false;

    for (let k = fillIdx + 1; k < n; k++) {
      const h = highs[k];
      const l = lows[k];

      if (isBuy) {
        if (l <= slP) {
          tr.status = "LOSS_SL";
          tr.win = false;
          tr.exit_time = times[k];
          tr.exit_price = slP;
          tr.pnl_pips = Number((-sig.risk_pips).toFixed(1));
          tr.pnlPips = tr.pnl_pips;
          tr.exit_reason = "SL_HIT";
          isFinished = true;
          break;
        }
        if (h >= tp1P) {
          tr.status = "WIN_TP";
          tr.win = true;
          tr.exit_time = times[k];
          tr.exit_price = tp1P;
          tr.pnl_pips = Number((sig.reward_pips).toFixed(1));
          tr.pnlPips = tr.pnl_pips;
          tr.exit_reason = "TP1_HIT";
          isFinished = true;
          break;
        }
      } else {
        if (h >= slP) {
          tr.status = "LOSS_SL";
          tr.win = false;
          tr.exit_time = times[k];
          tr.exit_price = slP;
          tr.pnl_pips = Number((-sig.risk_pips).toFixed(1));
          tr.pnlPips = tr.pnl_pips;
          tr.exit_reason = "SL_HIT";
          isFinished = true;
          break;
        }
        if (l <= tp1P) {
          tr.status = "WIN_TP";
          tr.win = true;
          tr.exit_time = times[k];
          tr.exit_price = tp1P;
          tr.pnl_pips = Number((sig.reward_pips).toFixed(1));
          tr.pnlPips = tr.pnl_pips;
          tr.exit_reason = "TP1_HIT";
          isFinished = true;
          break;
        }
      }
    }

    if (!isFinished) {
      tr.status = "RUNNING";
      tr.exit_time = times[n - 1];
      const currP = closes[n - 1];
      const pnlDiff = isBuy ? currP - entryP : entryP - currP;
      tr.pnl_pips = Number((pnlDiff * pipMultiplier).toFixed(1));
      tr.pnlPips = tr.pnl_pips;
      tr.pnl_diff = pnlDiff;
      tr.win = tr.pnl_pips > 0;
    } else {
      tr.pnl_diff = Number((tr.pnl_pips / pipMultiplier).toFixed(decimals));
    }

    completedTrades.push(tr);

    const setupTag = sig.setup_type || "BV";
    const maTag = sig.ma_filter ? ` [MA${maPeriod} ✓]` : "";
    const actText = (isLimit ? `LIMIT ${setupTag} ` : `${setupTag} `) + (isBuy ? "BUY" : "SELL") + maTag;
    chartMarkers.push({
      time: sig.time,
      position: isBuy ? "belowBar" : "aboveBar",
      color: isBuy ? "#06b6d4" : "#f43f5e",
      shape: isBuy ? "arrowUp" : "arrowDown",
      text: actText,
      id: `bv_${sig.time}`,
      size: 1,
    });
  }

  const latestSignal = completedTrades.length > 0 ? completedTrades[completedTrades.length - 1] : null;
  if (latestSignal) {
    latestSignal.bars_len = n;
    latestSignal.bars_ago = n - 1 - (latestSignal.index || 0);
    latestSignal.is_fresh = (latestSignal.index || 0) >= n - 2;
  }

  // 4. Visual Lines
  const visualLines = [];
  if (latestSignal) {
    const endT = times[n - 1];
    const tpVal = latestSignal.tp || latestSignal.tp1;
    const isLimit = latestSignal.entry_mode === "LIMIT";
    const entryLbl = isLimit ? `BV LIMIT (${latestSignal.setup_type || ''}): ${latestSignal.entry}` : `BV ENTRY (${latestSignal.setup_type || ''}): ${latestSignal.entry}`;
    visualLines.push({
      x1: latestSignal.time,
      x2: endT,
      y1: latestSignal.entry,
      y2: latestSignal.entry,
      color: isLimit ? "#f59e0b" : "#06b6d4",
      width: 1.5,
      style: isLimit ? 2 : 0,
      label: entryLbl,
    });
    visualLines.push({
      x1: latestSignal.time,
      x2: endT,
      y1: latestSignal.sl,
      y2: latestSignal.sl,
      color: "#ef4444",
      width: 1.5,
      style: 2,
      label: `SL: ${latestSignal.sl} (-${latestSignal.risk_pips || latestSignal.riskPips}p)`,
    });
    visualLines.push({
      x1: latestSignal.time,
      x2: endT,
      y1: tpVal,
      y2: tpVal,
      color: "#10b981",
      width: 1.5,
      style: 2,
      label: `TP (1:${r1}R): ${tpVal}`,
    });
  }

  // 5. Backtest statistics
  const computeStats = (trList) => {
    const finishedTrades = trList.filter((t) => t.status === "WIN_TP" || t.status === "LOSS_SL");
    const total = finishedTrades.length;
    if (total === 0) {
      return {
        total_trades: 0,
        win_rate: 0,
        wins: 0,
        losses: 0,
        net_pnl: 0,
        net_pnl_pips: 0,
        profit_factor: 1.0,
        avg_rr: `1:${r1}`,
        trades: [],
        max_win_streak: 0,
        max_loss_streak: 0,
        streaks: { max_win_streak: 0, max_loss_streak: 0 }
      };
    }

    const wins = finishedTrades.filter((t) => t.win).length;
    const losses = finishedTrades.filter((t) => !t.win).length;
    const winRate = total > 0 ? Math.round((wins / total) * 1000) / 10 : 0;
    const netPnl = Math.round(finishedTrades.reduce((sum, t) => sum + (t.pnlPips ?? t.pnl_pips ?? 0), 0) * 10) / 10;

    const grossProfit = finishedTrades.filter((t) => (t.pnlPips ?? t.pnl_pips ?? 0) > 0).reduce((sum, t) => sum + (t.pnlPips ?? t.pnl_pips ?? 0), 0);
    const grossLoss = Math.abs(finishedTrades.filter((t) => (t.pnlPips ?? t.pnl_pips ?? 0) < 0).reduce((sum, t) => sum + (t.pnlPips ?? t.pnl_pips ?? 0), 0));
    const profitFactor = grossLoss > 0 ? Math.round((grossProfit / grossLoss) * 100) / 100 : (grossProfit > 0 ? 99.9 : 1.0);

    let wMaxWin = 0;
    let wMaxLoss = 0;
    let wCurWin = 0;
    let wCurLoss = 0;
    finishedTrades.forEach((t) => {
      if (t.status === "WIN_TP") {
        wCurWin++;
        wCurLoss = 0;
        if (wCurWin > wMaxWin) wMaxWin = wCurWin;
      } else if (t.status === "LOSS_SL") {
        wCurLoss++;
        wCurWin = 0;
        if (wCurLoss > wMaxLoss) wMaxLoss = wCurLoss;
      }
    });

    return {
      total_trades: total,
      win_rate: winRate,
      wins,
      losses,
      net_pnl: netPnl,
      net_pnl_pips: netPnl,
      profit_factor: profitFactor,
      avg_rr: `1:${r1}`,
      trades: finishedTrades,
      max_win_streak: wMaxWin,
      max_loss_streak: wMaxLoss,
      streaks: { max_win_streak: wMaxWin, max_loss_streak: wMaxLoss }
    };
  };

  // Streaks
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let curWin = 0;
  let curLoss = 0;
  completedTrades.forEach((t) => {
    if (t.status === "WIN_TP") {
      curWin++;
      curLoss = 0;
      if (curWin > maxWinStreak) maxWinStreak = curWin;
    } else if (t.status === "LOSS_SL") {
      curLoss++;
      curWin = 0;
      if (curLoss > maxLossStreak) maxLossStreak = curLoss;
    }
  });

  // Calendar Day Window Calculation (ICT / UTC+7)
  const nowMs = Date.now();
  const nowVnDate = new Date(nowMs + 7 * 3600 * 1000);
  const vnYear = nowVnDate.getUTCFullYear();
  const vnMonth = nowVnDate.getUTCMonth();
  const vnDay = nowVnDate.getUTCDate();
  const todayStartTs = Math.floor(Date.UTC(vnYear, vnMonth, vnDay, 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const tradesToday = completedTrades.filter((t) => t.time >= todayStartTs);
  const trades3d = completedTrades.filter((t) => t.time >= threeDaysStartTs);
  const trades7d = completedTrades.filter((t) => t.time >= sevenDaysStartTs);
  const trades30d = completedTrades.filter((t) => t.time >= thirtyDaysStartTs);

  const backtestWindows = {
    today: computeStats(tradesToday),
    "24h": computeStats(tradesToday),
    "3d": computeStats(trades3d),
    "7d": computeStats(trades7d),
    "30d": computeStats(trades30d),
    all: computeStats(completedTrades),
  };

  // Market Bias
  let marketBias = "NEUTRAL";
  if (ema20.length >= 6) {
    const lastEma = ema20[ema20.length - 1];
    const prevEma = ema20[ema20.length - 4];
    const lastClose = closes[closes.length - 1];
    if (lastEma > prevEma && lastClose > lastEma) {
      marketBias = "BULLISH";
    } else if (lastEma < prevEma && lastClose < lastEma) {
      marketBias = "BEARISH";
    }
  }

  const emaLine = bars.map((b, idx) => ({
    time: times[idx],
    value: Number(ema20[idx].toFixed(decimals)),
  }));

  const maLine = bars.map((b, idx) => ({
    time: times[idx],
    value: Number((maSeries[idx] || 0).toFixed(decimals)),
  }));

  return {
    ema20: emaLine,
    ma_series: maLine,
    maSeries: maLine,
    signals: completedTrades,
    recent_signals: completedTrades,
    all_signals: completedTrades,
    latest_signal: latestSignal,
    latestSignal,
    chart_markers: chartMarkers,
    chartMarkers,
    visual_lines: visualLines,
    visualLines,
    barriers: allBarriers.slice(-15),
    trendlines: allTrendlines.slice(-15),
    buildup_boxes: allBuildups.slice(-25),
    buildupBoxes: allBuildups.slice(-25),
    market_bias: marketBias,
    streaks: { max_win_streak: maxWinStreak, max_loss_streak: maxLossStreak },
    backtest: backtestWindows.all,
    backtest_windows: backtestWindows,
  };
}
